//src/components/BillingReviewAction.js
import React, { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  FileSearch,
  FileText,
  History,
  Loader2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import axios from "../axiosConfig";
import ModalPortal from "./ModalPortal";

import "../styles/BillingReviewAction.css";

const positiveId = (value) => {
  const text = String(value ?? "");

  if (!/^[1-9]\d*$/.test(text)) {
    return null;
  }

  const number = Number(text);

  return Number.isSafeInteger(number)
    ? number
    : null;
};

const formatMoney = (value, locale, fallback) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return fallback;
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  }).format(amount);
};

const getLocale = (language) => {
  const normalized = String(
    language || "pt"
  ).toLowerCase();

  if (normalized.startsWith("fr")) {
    return "fr-FR";
  }

  if (normalized.startsWith("es")) {
    return "es-ES";
  }

  if (normalized.startsWith("en")) {
    return "en-GB";
  }

  return "pt-PT";
};

const formatDateTime = (
  value,
  locale,
  fallback
) => {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getApiErrorKey = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.error;

  if (
    status === 503 ||
    code === "billing_approval_not_enabled"
  ) {
    return "apiDisabled";
  }

  if (status === 401) {
    return "unauthorized";
  }

  if (status === 403) {
    return "forbidden";
  }

  if (
    status === 404 ||
    code === "load_not_found"
  ) {
    return "loadNotFound";
  }

  if (
    status === 409 &&
    code === "load_not_completed"
  ) {
    return "loadNotCompleted";
  }

  if (
    status === 409 &&
    code === "active_load_history_not_found"
  ) {
    return "historyNotFound";
  }

  return "requestFailed";
};

const getBillingReviewTheme = () => {
  if (typeof document === "undefined") {
    return "dark";
  }

  const root = document.documentElement;
  const body = document.body;

  const themeAttr = String(
    root?.getAttribute("data-theme") ||
    body?.getAttribute("data-theme") ||
    ""
  ).toLowerCase();

  const classBlob = String(
    `${root?.className || ""} ${body?.className || ""}`
  ).toLowerCase();

  if (
    themeAttr === "light" ||
    classBlob.includes("light-mode") ||
    classBlob.includes("theme-light") ||
    /\blight\b/.test(classBlob)
  ) {
    return "light";
  }

  return "dark";
};

const billingRequestStorageKey = (loadId) =>
  `loadevo:billing:approval:55:${loadId}`;

const readPendingBillingRequest = (loadId) => {
  if (!loadId || typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage.getItem(
      billingRequestStorageKey(loadId)
    );
  } catch (error) {
    return null;
  }
};

const savePendingBillingRequest = (loadId, requestId) => {
  if (!loadId || !requestId || typeof window === "undefined") {
    return false;
  }

  try {
    window.localStorage.setItem(
      billingRequestStorageKey(loadId),
      requestId
    );

    return (
      readPendingBillingRequest(loadId) === requestId
    );
  } catch (error) {
    return false;
  }
};

const clearPendingBillingRequest = (loadId, requestId) => {
  if (!loadId || !requestId || typeof window === "undefined") {
    return false;
  }

  try {
    if (
      readPendingBillingRequest(loadId) !== requestId
    ) {
      return false;
    }

    window.localStorage.removeItem(
      billingRequestStorageKey(loadId)
    );

    return (
      readPendingBillingRequest(loadId) === null
    );
  } catch (error) {
    return false;
  }
};

const initialServerState = {
  loading: false,
  loaded: false,
  approval: null,
  persisted: false,
  canApprove: false,
  commercialApprovalBlockers: [],
  fiscalStatus: "UNKNOWN",
  fiscalPreparation: null,
  historyId: null,
  errorKey: null,
  events: [],
  eventsError: false,
};

const initialFiscalDocumentsState = {
  loading: false,
  loaded: false,
  documents: [],
  hasMore: false,
  error: false,
};

export default function BillingReviewAction({
  history,
  compact = false,
  onBillingChanged,
}) {
  const { t, i18n } = useTranslation("billing_review");

  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [reviewed, setReviewed] = useState(false);

  const [commercialSaving, setCommercialSaving] = useState(false);
  const [commercialError, setCommercialError] = useState(null);
  const [commercialSuccess, setCommercialSuccess] = useState(false);
  const [commercialOutcomePending, setCommercialOutcomePending] = useState(false);

  const [billingRefreshKey, setBillingRefreshKey] = useState(0);
  const [fiscalDetailsExpanded, setFiscalDetailsExpanded] = useState(false);
  const [fiscalDocumentType, setFiscalDocumentType] = useState("");

  const commercialRequestInFlightRef = useRef(false);


  const [serverState, setServerState] = useState(
    initialServerState
  );

  const [fiscalDocumentsState, setFiscalDocumentsState] =
    useState(initialFiscalDocumentsState);

  const locale = getLocale(
    i18n.resolvedLanguage || i18n.language
  );

  const translate = (key, options = {}) =>
    t(key, options);

  const load = history?.load || {};

  const loadId = positiveId(
    history?.loadId ?? load?.id
  );

  const loadNumber =
    history?.loadNumber ??
    load?.loadNumber ??
    loadId ??
    "—";

  const status = String(
    load?.status || history?.status || ""
  ).toUpperCase();

  const completed = status === "COMPLETED";

  const client =
    history?.client ||
    load?.industryClient?.companyName ||
    load?.industryClient?.name ||
    load?.carrierClient?.companyName ||
    load?.carrierClient?.name ||
    translate("notIdentified");

  const price =
    history?.price != null &&
    history.price !== ""
      ? history.price
      : load?.price;

  const pickups =
    history?.pickupCount ??
    history?.pickupList?.length ??
    load?.pickupDeliveries?.filter(
      (point) =>
        String(point.type).toUpperCase() === "PICKUP"
    ).length ??
    null;

  const deliveries =
    history?.deliveryCount ??
    history?.deliveryList?.length ??
    load?.pickupDeliveries?.filter(
      (point) =>
        String(point.type).toUpperCase() === "DELIVERY"
    ).length ??
    null;

  const canReview = completed && Boolean(loadId);

  const isTest =
    typeof window !== "undefined" &&
    window.location.hostname ===
      "test-app.loadevo.com";

  useEffect(() => {
    if (!open || !loadId || !isTest) {
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setServerState({
      ...initialServerState,
      loading: true,
    });

    const fetchBillingStatus = async () => {
      try {

        const response = await axios.get(
          `/load-billing-approvals/${loadId}`,
          {
            params: fiscalDocumentType
              ? { fiscalDocumentType }
              : undefined,
            signal: controller.signal,
          }
        );


        if (!active) return;

        const data = response?.data || {};

        if (data.ok !== true) {
          throw new Error(
            "Invalid billing approval response"
          );
        }

        setServerState((previous) => ({
          ...previous,
          loading: false,
          loaded: true,
          approval: data.approval || null,
          persisted:
            data.approvalPersisted === true,
          canApprove:
            data.canApprove === true,
          commercialApprovalBlockers:
            Array.isArray(data.commercialApprovalBlockers)
              ? data.commercialApprovalBlockers
              : [],
          fiscalStatus:
            data.fiscalStatus || "UNKNOWN",
          fiscalPreparation:
            data.fiscalPreparation || null,
          historyId: data.historyId ?? null,
          errorKey: null,
        }));

        try {
          const eventsResponse = await axios.get(
            `/load-billing-approvals/${loadId}/events`,
            {
              signal: controller.signal,
            }
          );

          if (!active) return;

          const eventsData =
            eventsResponse?.data || {};

          setServerState((previous) => ({
            ...previous,
            events:
              eventsData.ok === true &&
              Array.isArray(eventsData.events)
                ? eventsData.events
                : [],
            eventsError:
              eventsData.ok !== true,
          }));
        } catch (eventsError) {
          if (!active) return;

          setServerState((previous) => ({
            ...previous,
            eventsError: true,
          }));
        }
      } catch (error) {
        if (!active) return;

        setServerState((previous) => ({
          ...previous,
          loading: false,
          loaded: false,
          errorKey: getApiErrorKey(error),
        }));
      }
    };

    fetchBillingStatus();

    return () => {
      active = false;
      controller.abort();
    };
  }, [open, loadId, isTest, billingRefreshKey, fiscalDocumentType]);

  useEffect(() => {
    if (!open || !loadId || !isTest) {
      return undefined;
    }

    let active = true;
    let checking = false;
    const controller = new AbortController();

    const recoverPendingApproval = async () => {
      if (!active || checking) {
        return;
      }

      const requestId = readPendingBillingRequest(loadId);

      if (!requestId) {
        return;
      }

      checking = true;

      setCommercialOutcomePending(true);
      setCommercialError("approvalStatusUncertain");

      try {
        // Primeiro: confirmar a operação exata
        // identificada pelo requestId.

        const operationResponse = await axios.get(
          `/load-billing-approvals/${loadId}/operations/${requestId}`,
          {
            signal: controller.signal,
          }
        );

        if (!active) {
          return;
        }

        const operation = operationResponse?.data || {};

        const operationConfirmed =
          operation.ok === true &&
          operation.requestId === requestId &&
          operation.outcome === "COMMITTED" &&
          operation.confirmed === true &&
          operation.event?.eventType === "APPROVED" &&
          operation.event?.newStatus === "APPROVED";

        if (!operationConfirmed) {
          return;
        }

        // Segundo: confirmar o estado comercial
        // canónico da carga no servidor.

        const statusResponse = await axios.get(
          `/load-billing-approvals/${loadId}`,
          {
            params: fiscalDocumentType
              ? { fiscalDocumentType }
              : undefined,
            signal: controller.signal,
          }
        );

        if (!active) {
          return;
        }

        const verified = statusResponse?.data || {};

        const approvalConfirmed =
          verified.ok === true &&
          Number(verified.loadId) === loadId &&
          verified.approvalPersisted === true &&
          String(
            verified.approval?.status || ""
          ).toUpperCase() === "APPROVED" &&
          Number(verified.approval?.id) ===
            Number(operation.event.approvalId);

        if (!approvalConfirmed) {
          return;
        }

        // Uma resposta antiga não pode limpar a
        // chave de uma operação posterior.

        if (
          readPendingBillingRequest(loadId) !== requestId
        ) {
          return;
        }

        const cleared = clearPendingBillingRequest(
          loadId,
          requestId
        );

        if (!cleared) {
          return;
        }

        if (!active) {
          return;
        }

        setServerState((previous) => ({
          ...previous,
          loading: false,
          loaded: true,
          approval: verified.approval,
          persisted: true,
          fiscalStatus: verified.fiscalStatus || "UNKNOWN",
          fiscalPreparation: verified.fiscalPreparation || null,
          historyId: verified.historyId ?? null,
          canApprove: false,
          errorKey: null,
        }));

        setCommercialOutcomePending(false);
        setCommercialError(null);
        setCommercialSuccess(true);
        setReviewed(false);
        setBillingRefreshKey((previous) => previous + 1);

        if (typeof onBillingChanged === "function") {
          onBillingChanged(loadId);
        }
      } catch (error) {
        // Falha HTTP ou estado incerto:
        // preservar requestId e bloquear novo POST.
      } finally {
        checking = false;
      }
    };

    const existingRequest = readPendingBillingRequest(loadId);

    if (existingRequest) {
      setCommercialOutcomePending(true);
      setCommercialError("approvalStatusUncertain");
      recoverPendingApproval();
    } else {
      setCommercialOutcomePending(false);
    }

    const interval = window.setInterval(
      recoverPendingApproval,
      5000
    );

    const handleStorageChange = (event) => {
      if (
        event.key !== billingRequestStorageKey(loadId)
      ) {
        return;
      }

      if (event.newValue) {
        setCommercialOutcomePending(true);
        setCommercialError("approvalStatusUncertain");
        recoverPendingApproval();
        return;
      }

      // Outra aba poderá ter confirmado a operação.
      // Não assumir sucesso sem consulta ao Backend.

      setCommercialOutcomePending(false);
      setCommercialSuccess(false);
      setBillingRefreshKey((previous) => previous + 1);
    };

    window.addEventListener(
      "storage",
      handleStorageChange
    );

    return () => {
      active = false;
      controller.abort();
      window.clearInterval(interval);
      window.removeEventListener(
        "storage",
        handleStorageChange
      );
    };
  }, [open, loadId, isTest, fiscalDocumentType]);

  useEffect(() => {
    if (!open || !loadId || !isTest) {
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setFiscalDocumentsState({
      ...initialFiscalDocumentsState,
      loading: true,
    });

    const fetchFiscalDocuments = async () => {
      try {
        const response = await axios.get(
          "/fiscal-documents",
          {
            params: {
              sourceLoadId: loadId,
              limit: 50,
            },
            signal: controller.signal,
          }
        );

        if (!active) return;

        const data = response?.data || {};

        if (
          data.ok !== true ||
          !Array.isArray(data.documents)
        ) {
          throw new Error(
            "Invalid fiscal documents response"
          );
        }

        // Defensive verification: never associate
        // a document with a different Load.
        const documents = data.documents.filter(
          (document) =>
            document.isSimulation !== true &&
            positiveId(document.sourceLoadId) === loadId
        );

        setFiscalDocumentsState({
          loading: false,
          loaded: true,
          documents,
          hasMore: data.hasMore === true,
          error: false,
        });
      } catch (error) {
        if (!active) return;

        setFiscalDocumentsState({
          ...initialFiscalDocumentsState,
          loaded: false,
          error: true,
        });
      }
    };

    fetchFiscalDocuments();

    return () => {
      active = false;
      controller.abort();
    };
  }, [open, loadId, isTest]);

  if (!isTest) {
    return null;
  }

  const close = () => {
    if (commercialSaving) {
      return;
    }

    setOpen(false);
    setConfirmed(false);
    setReviewed(false);
    setFiscalDocumentType("");
    setCommercialError(null);
    setCommercialSuccess(false);
  };

  const openReview = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!canReview) {
      return;
    }

    setConfirmed(false);
    setReviewed(false);
    setFiscalDocumentType("");
    setCommercialError(null);
    setCommercialSuccess(false);
    setOpen(true);
  };

  const approveCommercially = async () => {
    if (
      commercialRequestInFlightRef.current ||
      commercialSaving ||
      commercialOutcomePending ||
      readPendingBillingRequest(loadId) ||
      !confirmed ||
      !isTest ||
      !loadId ||
      !serverState.loaded ||
      serverState.errorKey ||
      serverState.canApprove !== true
    ) {
      return;
    }

    commercialRequestInFlightRef.current = true;

    setCommercialSaving(true);
    setCommercialOutcomePending(true);
    setCommercialError(null);
    setCommercialSuccess(false);

    let requestId = null;

    try {
      if (
        typeof window === "undefined" ||
        typeof window.crypto?.randomUUID !== "function"
      ) {
        setCommercialError("approvalStatusUncertain");
        setCommercialOutcomePending(false);
        return;
      }

      requestId = window.crypto.randomUUID();

      const saved = savePendingBillingRequest(
        loadId,
        requestId
      );

      if (!saved) {
        setCommercialError("approvalStatusUncertain");
        setCommercialOutcomePending(false);
        return;
      }

      try {
        await axios.post(
          `/load-billing-approvals/${loadId}/approve`,
          {
            requestId,
          }
        );
      } catch (error) {
        // O POST pode ter sido executado,
        // apesar de o browser não receber resposta.
        // Nunca repetir automaticamente.
      }

      const recoveryResponse = await axios.get(
        `/load-billing-approvals/${loadId}/operations/${requestId}`
      );

      const recovery = recoveryResponse?.data || {};

      const operationConfirmed =
        recovery.ok === true &&
        recovery.requestId === requestId &&
        recovery.outcome === "COMMITTED" &&
        recovery.confirmed === true &&
        recovery.event?.eventType === "APPROVED" &&
        recovery.event?.newStatus === "APPROVED";

      if (!operationConfirmed) {
        setCommercialError("approvalStatusUncertain");
        return;
      }

      const verificationResponse = await axios.get(
        `/load-billing-approvals/${loadId}`,
        {
          params: fiscalDocumentType
            ? { fiscalDocumentType }
            : undefined,
        }
      );

      const verified = verificationResponse?.data || {};

      const persistedApproval =
        verified.ok === true &&
        Number(verified.loadId) === loadId &&
        verified.approvalPersisted === true &&
        String(
          verified.approval?.status || ""
        ).toUpperCase() === "APPROVED" &&
        Number.isSafeInteger(
          Number(recovery.event?.approvalId)
        ) &&
        Number(recovery.event.approvalId) > 0 &&
        Number(verified.approval?.id) ===
          Number(recovery.event.approvalId);

      if (!persistedApproval) {
        // O evento e o estado comercial canónico
        // têm de identificar a mesma aprovação.
        // Sem essa confirmação, manter requestId
        // e impedir uma segunda operação.
        setCommercialError("approvalStatusUncertain");
        return;
      }

      const cleared = clearPendingBillingRequest(
        loadId,
        requestId
      );

      if (!cleared) {
        setCommercialError("approvalStatusUncertain");
        return;
      }

      setServerState((previous) => ({
        ...previous,
        loaded: true,
        loading: false,
        approval: verified.approval,
        persisted: true,
        fiscalStatus: verified.fiscalStatus || "UNKNOWN",
        historyId: verified.historyId ?? null,
        canApprove: false,
        errorKey: null,
      }));

      setCommercialSuccess(true);
      setCommercialError(null);
      setCommercialOutcomePending(false);
      setReviewed(false);
      setBillingRefreshKey((previous) => previous + 1);

      if (typeof onBillingChanged === "function") {
        onBillingChanged(loadId);
      }
    } catch (error) {
      // Nunca remover uma operação pendente
      // apenas porque a consulta HTTP falhou.
      setCommercialError("approvalStatusUncertain");
    } finally {
      commercialRequestInFlightRef.current = false;
      setCommercialSaving(false);
    }
  };

  const approvalStatus = String(
    serverState.approval?.status || "PENDING"
  ).toUpperCase();

  const isApproved =
    serverState.loaded &&
    approvalStatus === "APPROVED";

  const isPending =
    serverState.loaded &&
    approvalStatus === "PENDING";

  const approvalAvailable =
    serverState.loaded &&
    !serverState.errorKey;

  const approvalDate = formatDateTime(
    serverState.approval?.approvedAt,
    locale,
    translate("notAvailable")
  );

  const approvalUserId =
    serverState.approval?.approvedByUserId;

  const reviewedBy = approvalUserId
    ? translate("userId", {
        id: approvalUserId,
      })
    : translate("notAvailable");

  const fiscalStatus =
    String(serverState.fiscalStatus || "UNKNOWN")
      .toUpperCase();

  const fiscalStatusLabels = {
    AVAILABLE: translate("fiscalAvailable", {
      defaultValue: "Disponível para faturação",
    }),
    ISSUING: translate("fiscalIssuing", {
      defaultValue: "Em processamento fiscal",
    }),
    ISSUED: translate("fiscalIssued", {
      defaultValue: "Fatura emitida",
    }),
    RECOVERY_REQUIRED: translate("fiscalRecoveryRequired", {
      defaultValue: "Reconciliação necessária",
    }),
    UNKNOWN: translate("fiscalUnknown", {
      defaultValue: "Estado fiscal desconhecido",
    }),
  };

  const fiscalLabel =
    fiscalStatusLabels[fiscalStatus] ||
    translate("fiscalUnverified", {
      defaultValue: "Estado fiscal não verificado",
    });

  const renderApprovalStatus = () => {
    if (serverState.loading) {
      return (
        <span className="billing-review-api-state">
          <Loader2
            size={15}
            className="billing-review-spinner"
          />
          {translate("loading")}
        </span>
      );
    }

    if (serverState.errorKey) {
      return (
        <span className="billing-review-api-state is-unavailable">
          <AlertCircle size={15} />
          {translate("statusUnavailable")}
        </span>
      );
    }

    if (isApproved) {
      return (
        <span className="billing-review-api-state is-approved">
          <CheckCircle2 size={15} />
          {translate("approved")}
        </span>
      );
    }

    if (isPending) {
      return (
        <span className="billing-review-api-state is-pending">
          {translate("pendingApproval")}
        </span>
      );
    }

    return (
      <span className="billing-review-api-state">
        {translate("notAvailable")}
      </span>
    );
  };

  return (
    <>
      <button
        type="button"
        className={`billing-review-trigger ${
          compact ? "is-compact" : ""
        }`}
        onClick={openReview}
        disabled={!canReview}
        title={
          canReview
            ? translate("triggerTooltip")
            : translate("requiresCompleted")
        }
        aria-label={translate("trigger")}
      >
        <FileSearch size={15} />

        {!compact && (
          <span>{translate("trigger")}</span>
        )}
      </button>

      {open && (
        <ModalPortal>
          <div
            className="billing-review-backdrop"
            onMouseDown={(event) => {
              if (
                event.target === event.currentTarget
              ) {
                close();
              }
            }}
          >
            <section
              className={`billing-review-modal billing-review-theme-${getBillingReviewTheme()}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="billing-review-title"
              onClick={(event) =>
                event.stopPropagation()
              }
            >
              <header className="billing-review-header">
                <div className="billing-review-heading">
                  <span className="billing-review-heading-icon">
                    <ClipboardCheck size={21} />
                  </span>

                  <div>
                    <span className="billing-review-eyebrow">
                      {translate("eyebrow")}
                    </span>

                    <h2 id="billing-review-title">
                      {translate("title")}
                    </h2>

                    <p>
                      {translate("loadReference", {
                        number: loadNumber,
                      })}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="billing-review-close"
                  onClick={close}
                  aria-label={translate("close")}
                >
                  <X size={19} />
                </button>
              </header>

              <div className="billing-review-content">
                <div className="billing-review-hero">
                  <div className="billing-review-hero-main">
                    <span className="billing-review-hero-label">
                      {translate("transportValue")}
                    </span>

                    <strong className="billing-review-hero-price">
                      {formatMoney(
                        price,
                        locale,
                        translate("notDefined")
                      )}
                    </strong>

                    <span className="billing-review-hero-client">
                      {translate("clientLabel")} · {client}
                    </span>
                  </div>

                  <span className="billing-review-status">
                    <CheckCircle2 size={15} />
                    {translate("completed")}
                  </span>
                </div>

                <div className="billing-review-details">
                  <div>
                    <span>
                      {translate("pickupPoints")}
                    </span>
                    <strong>{pickups ?? "—"}</strong>
                  </div>

                  <div>
                    <span>
                      {translate("deliveryPoints")}
                    </span>
                    <strong>{deliveries ?? "—"}</strong>
                  </div>

                  <div>
                    <span>
                      {translate("vatClassification")}
                    </span>

                    <strong className="billing-review-pending">
                      {serverState.fiscalPreparation?.vat
                        ?.configuredClientRate != null
                        ? `${serverState.fiscalPreparation.vat.configuredClientRate}% · Por validar`
                        : "Por classificar"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      {translate("fiscalDocument")}
                    </span>

                    <strong>
                      {fiscalDocumentsState.loading
                        ? translate("loading")
                        : fiscalDocumentsState.error
                          ? translate("fiscalLookupFailed")
                          : fiscalDocumentsState.loaded
                            ? fiscalDocumentsState.documents.length > 0
                              ? fiscalDocumentsState.documents[0]
                                  .documentNumber ||
                                translate("notAvailable")
                              : translate("noFiscalDocuments")
                            : translate("notVerified")}
                    </strong>
                  </div>
                </div>


                {serverState.fiscalPreparation?.ok === true && (
                  <div className="billing-review-server-panel">
                    <div className="billing-review-server-heading">
                      <span>
                        <FileSearch size={16} />
                        Preparação fiscal
                      </span>

                      <span className="billing-review-api-state is-pending">
                        Por validar
                      </span>
                    </div>

                    <div
                      className="billing-review-document-type"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                        padding: "10px 12px",
                        marginBottom: 12,
                        border: "1px solid #dbe5f1",
                        borderRadius: 12,
                      }}
                    >
                      <div>
                        <strong style={{ fontSize: 13 }}>
                          Tipo de documento
                        </strong>
                        <div
                          style={{
                            fontSize: 12,
                            opacity: 0.7,
                            marginTop: 3,
                          }}
                        >
                          Seleção para preparação fiscal
                        </div>
                      </div>

                      <select
                        aria-label="Tipo de documento fiscal"
                        value={fiscalDocumentType}
                        disabled={serverState.loading}
                        onChange={(event) =>
                          setFiscalDocumentType(event.target.value)
                        }
                        style={{
                          minWidth: 170,
                          padding: "9px 12px",
                          borderRadius: 10,
                          border: "1px solid #dbe5f1",
                          background: "transparent",
                          color: "inherit",
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        <option value="">Por definir</option>
                        <option value="FT">FT · Fatura</option>
                        <option value="FS">
                          FS · Fatura simplificada
                        </option>
                      </select>
                    </div>

                    <div className="billing-review-server-meta">

                      <div>
                        <span>Cliente</span>
                        <strong>
                          {serverState.fiscalPreparation.customer?.name ||
                            "Não identificado"}
                        </strong>
                      </div>

                      <div>
                        <span>Identificação fiscal</span>
                        <strong>
                          {serverState.fiscalPreparation.customer
                            ?.taxIdentification || "Em falta"}
                        </strong>
                      </div>

                      <div>
                        <span>Valor do transporte</span>
                        <strong>
                          {formatMoney(
                            serverState.fiscalPreparation.transport?.price,
                            locale,
                            "Por definir"
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>Enquadramento IVA</span>
                        <strong>
                          {serverState.fiscalPreparation.vat?.verified
                            ? "Validado"
                            : "Validação necessária"}
                        </strong>
                      </div>
                    </div>

                    {Array.isArray(serverState.fiscalPreparation.blockers) &&
                      serverState.fiscalPreparation.blockers.length > 0 && (
                        <div className="billing-review-fiscal-checks">
                          {serverState.fiscalPreparation.blockers.includes(
                            "fiscal_reservation_unavailable"
                          ) && (
                            <div
                              className="billing-review-fiscal-safety"
                              role="status"
                            >
                              <AlertCircle size={16} />

                              <div>
                                <strong>
                                  {translate(
                                    "fiscalReservationWarningTitle"
                                  )}
                                </strong>

                                <span>
                                  {translate(
                                    "fiscalReservationWarningDescription"
                                  )}
                                </span>
                              </div>
                            </div>
                          )}

                          <button
                            type="button"
                            className="billing-review-fiscal-toggle"
                            aria-expanded={fiscalDetailsExpanded}
                            onClick={() =>
                              setFiscalDetailsExpanded((previous) => !previous)
                            }
                          >
                            <span className="billing-review-fiscal-toggle-title">
                              <AlertCircle size={15} />

                              {translate("fiscalBlockersTitle", {
                                count:
                                  serverState.fiscalPreparation.blockers.length,
                              })}
                            </span>

                            <span className="billing-review-fiscal-toggle-action">
                              {translate(
                                fiscalDetailsExpanded
                                  ? "hideFiscalDetails"
                                  : "showFiscalDetails"
                              )}

                              <span aria-hidden="true">
                                {fiscalDetailsExpanded ? "−" : "+"}
                              </span>
                            </span>
                          </button>

                          {fiscalDetailsExpanded && (
                            <div className="billing-review-fiscal-check-list">
                              {serverState.fiscalPreparation.blockers.map(
                                (blocker, index) => (
                                  <div
                                    key={`${blocker}-${index}`}
                                    className="billing-review-fiscal-check"
                                  >
                                    <AlertCircle size={13} />

                                    <span>
                                      {translate(
                                        `fiscalBlockers.${blocker}`,
                                        {
                                          defaultValue: translate(
                                            "fiscalBlockerUnknown"
                                          ),
                                        }
                                      )}
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                )}

                <div className="billing-review-server-panel">
                  <div className="billing-review-server-heading">

                    <span>
                      <ClipboardCheck size={16} />
                      {translate("commercialApproval")}
                    </span>

                    {renderApprovalStatus()}
                  </div>

                  {serverState.errorKey && (
                    <div className="billing-review-server-message">
                      <AlertCircle size={16} />

                      <span>
                        {translate(
                          `errors.${serverState.errorKey}`
                        )}
                      </span>
                    </div>
                  )}

                  {approvalAvailable && (
                    <div className="billing-review-server-meta">
                      <div>
                        <span>
                          {translate("approvalOrigin")}
                        </span>

                        <strong>
                          {serverState.persisted
                            ? translate("serverRegistered")
                            : translate("notYetRegistered")}
                        </strong>
                      </div>

                      <div>
                        <span>
                          {translate("fiscalStatus")}
                        </span>

                        <strong>{fiscalLabel}</strong>
                      </div>
                    </div>
                  )}

                  {isApproved && (
                    <div className="billing-review-approved-meta">
                      <div>
                        <span>
                          {translate("approvedBy")}
                        </span>
                        <strong>{reviewedBy}</strong>
                      </div>

                      <div>
                        <span>
                          {translate("approvedAt")}
                        </span>
                        <strong>{approvalDate}</strong>
                      </div>
                    </div>
                  )}

                  {approvalAvailable &&
                    serverState.events.length > 0 && (
                      <div className="billing-review-events">
                        <div className="billing-review-events-title">
                          <History size={15} />
                          {translate("approvalHistory")}
                        </div>

                        {serverState.events.map(
                          (event) => {
                            const eventLabel =
                              event.eventType === "APPROVED"
                                ? translate("eventApproved")
                                : event.eventType === "REVOKED"
                                  ? translate("eventRevoked")
                                  : translate("eventUnknown");

                            return (
                              <div
                                key={event.id}
                                className="billing-review-event"
                              >
                                <div>
                                  <strong>{eventLabel}</strong>
                                  <span>
                                    {translate("userId", {
                                      id: event.actorUserId,
                                    })}
                                  </span>
                                </div>

                                <time>
                                  {formatDateTime(
                                    event.createdAt,
                                    locale,
                                    translate("notAvailable")
                                  )}
                                </time>

                                {event.reason && (
                                  <p>{event.reason}</p>
                                )}
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}

                  {approvalAvailable &&
                    serverState.eventsError && (
                      <div className="billing-review-server-message">
                        <AlertCircle size={15} />
                        <span>
                          {translate("eventsUnavailable")}
                        </span>
                      </div>
                    )}
                </div>

                <div className="billing-review-fiscal-panel">
                  <div className="billing-review-fiscal-heading">
                    <span>
                      <FileText size={17} />
                      {translate("fiscalDocumentsTitle")}
                    </span>

                    {fiscalDocumentsState.loaded &&
                      !fiscalDocumentsState.error && (
                        <span className="billing-review-fiscal-count">
                          {fiscalDocumentsState.documents.length}
                          {fiscalDocumentsState.hasMore ? "+" : ""}
                        </span>
                      )}
                  </div>

                  {fiscalDocumentsState.loading && (
                    <div className="billing-review-fiscal-message">
                      <Loader2
                        size={16}
                        className="billing-review-spinner"
                      />
                      {translate("loadingFiscalDocuments")}
                    </div>
                  )}

                  {fiscalDocumentsState.error && (
                    <div className="billing-review-fiscal-message is-error">
                      <AlertCircle size={16} />
                      {translate("fiscalLookupFailed")}
                    </div>
                  )}

                  {fiscalDocumentsState.loaded &&
                    fiscalDocumentsState.documents.length === 0 && (
                      <div className="billing-review-fiscal-message">
                        <FileSearch size={16} />
                        {translate(
                          fiscalDocumentsState.hasMore
                            ? "fiscalResultsIncomplete"
                            : "noFiscalDocuments"
                        )}
                      </div>
                    )}

                  {fiscalDocumentsState.documents.map(
                    (document) => {
                      const status = String(
                        document.status || ""
                      ).toUpperCase();

                      const statusLabel =
                        status === "ISSUED"
                          ? translate("fiscalIssued")
                          : status === "DRAFT"
                            ? translate("fiscalDraft")
                            : status === "CANCELLED"
                              ? translate("fiscalCancelled")
                              : translate("fiscalUnverified");

                      return (
                        <div
                          key={document.id}
                          className="billing-review-fiscal-document"
                        >
                          <div className="billing-review-fiscal-document-main">
                            <FileText size={17} />

                            <div>
                              <strong>
                                {document.documentNumber ||
                                  `${document.documentType || "—"} #${document.id}`}
                              </strong>

                              <span>
                                {document.issueDate ||
                                  translate("notAvailable")}
                              </span>
                            </div>
                          </div>

                          <div className="billing-review-fiscal-document-meta">
                            <span
                              className={`billing-review-fiscal-status is-${status.toLowerCase()}`}
                            >
                              {statusLabel}
                            </span>

                            <strong>
                              {formatMoney(
                                document.grossTotal,
                                locale,
                                translate("notAvailable")
                              )}
                            </strong>
                          </div>
                        </div>
                      );
                    }
                  )}

                  {fiscalDocumentsState.loaded &&
                    fiscalDocumentsState.hasMore && (
                      <div className="billing-review-fiscal-message">
                        <AlertCircle size={15} />
                        {translate("fiscalResultsIncomplete")}
                      </div>
                    )}
                </div>

                <div className="billing-review-notice">
                  <FileSearch size={17} />

                  <span>
                    {translate("testNotice")}
                  </span>
                </div>

                <label className="billing-review-confirmation">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) => {
                      setConfirmed(
                        event.target.checked
                      );
                      setReviewed(false);
                    }}
                  />

                  <span>
                    {translate("confirmation")}
                  </span>
                </label>

                {approvalAvailable &&
                  !isApproved &&
                  !serverState.canApprove && (
                    <div
                      className="billing-review-server-message"
                      role="status"
                    >
                      <AlertCircle size={18} />

                      <span>
                        {serverState.commercialApprovalBlockers.includes(
                          "fiscal_reservation_unavailable"
                        )
                          ? "A base de dados desta aplicação não permite verificar as reservas fiscais."
                          : serverState.commercialApprovalBlockers.includes(
                              "commercial_writes_not_enabled"
                            )
                            ? "A aprovação comercial não está ativada neste ambiente."
                            : serverState.commercialApprovalBlockers.includes(
                                "fiscal_reservation_exists"
                              )
                              ? "Esta carga já possui uma reserva fiscal."
                              : "A aprovação comercial ainda não está autorizada pelo servidor."}
                      </span>
                    </div>
                  )}

                {commercialSuccess && (
                  <div className="billing-review-success" role="status">
                    <CheckCircle2 size={18} />

                    <span>
                      {translate("commercialApprovalSaved", {
                        defaultValue:
                          "Aprovação comercial confirmada no servidor.",
                      })}
                    </span>
                  </div>
                )}

                {commercialError && (
                  <div
                    className="billing-review-server-message is-unavailable"
                    role="alert"
                  >
                    <AlertCircle size={18} />

                    <span>
                      {translate("commercialApprovalFailed", {
                        defaultValue:
                          "Não foi possível confirmar a aprovação. Consulta o estado atualizado antes de tentar novamente.",
                      })}
                    </span>
                  </div>
                )}
              </div>

              <footer className="billing-review-footer">
                <button
                  type="button"
                  className="billing-review-secondary"
                  onClick={close}
                  disabled={commercialSaving}
                >
                  {translate("close")}
                </button>


                <button
                  type="button"
                  className="billing-review-primary"
                  disabled={
                    !confirmed ||
                    commercialSaving ||
                    commercialOutcomePending ||
                    !approvalAvailable ||
                    !isPending ||
                    serverState.canApprove !== true
                  }
                  onClick={approveCommercially}
                  title={
                    serverState.canApprove !== true
                      ? translate("commercialApprovalUnavailable", {
                          defaultValue:
                            "A aprovação comercial ainda não está autorizada neste ambiente.",
                        })
                      : undefined
                  }
                >
                  {commercialSaving ? (
                    <Loader2
                      size={16}
                      className="billing-review-spinner"
                    />
                  ) : (
                    <CheckCircle2 size={16} />
                  )}

                  {translate("approveCommercially", {
                    defaultValue: "Aprovar comercialmente",
                  })}
                </button>
              </footer>
            </section>
          </div>
        </ModalPortal>
      )}
    </>
  );
}
